import {
	formatMillLengthIn,
	parseSawEntryQr,
} from "@/pages/sawlines/utils/entryBarcode";
import type {
	MillBundle,
	MillSection,
} from "./repositories/tagStationRepository";

export const TAG_STATION_ASSET_NAME = "Tag Station";

export const FIELDS = [
	["pcs", "Pieces"],
	["len", "Length"],
	["heat", "Heat"],
	["grade", "Grade"],
	["section", "Section"],
] as const;
export type Field = (typeof FIELDS)[number][0];

export interface Values {
	section: string;
	grade: string;
	heat: string;
	lengthIn: number;
	pieces: number;
}

// Inputs as typed; ft/inch/pcs stay strings so an empty box is not 0.
export interface Draft {
	section: string;
	grade: string;
	heat: string;
	ft: string;
	inch: string;
	pcs: string;
	comment: string;
}

// Per-bundle progress of the check, kept until Save and print.
export interface Work {
	draft: Draft;
	confirmed: Partial<Record<Field, true>>;
	editing: Partial<Record<Field, true>>;
}

export const notSent = (b: MillBundle) =>
	b.State === "Printed" &&
	["Failed", "Expired", "Cancelled"].includes(b.PrintStatus ?? "");

// queued: the printer may not be ready yet
export const printing = (b: MillBundle) =>
	b.State === "Printed" &&
	["Pending", "Processing"].includes(b.PrintStatus ?? "");

export const official = (b: MillBundle): Values => ({
	section: b.SectionName,
	grade: b.GradeName ?? "",
	heat: b.HeatNo ?? "",
	lengthIn: b.LengthIn,
	pieces: b.Pieces,
});

export const sent = (b: MillBundle): Values => ({
	section: b.SentSectionName,
	grade: b.SentGradeName ?? "",
	heat: b.SentHeatNo ?? "",
	lengthIn: b.SentLengthIn,
	pieces: b.SentPieces,
});

export const draftOf = (b: MillBundle): Draft => ({
	section: b.SectionName,
	grade: b.GradeName ?? "",
	heat: b.HeatNo ?? "",
	ft: String(Math.floor(b.LengthIn / 12)),
	inch: String(b.LengthIn % 12),
	pcs: String(b.Pieces),
	comment: b.Comment ?? "",
});

export const newWork = (b: MillBundle): Work => ({
	draft: draftOf(b),
	confirmed: {},
	editing: {},
});

export const valuesOf = (d: Draft): Values => ({
	section: d.section,
	grade: d.grade,
	heat: d.heat.trim(),
	lengthIn: (Number(d.ft) || 0) * 12 + (Number(d.inch) || 0),
	pieces: Number(d.pcs) || 0,
});

export const changed = (from: Values, to: Values): Field[] =>
	FIELDS.map(([f]) => f).filter(
		(f) =>
			({
				pcs: from.pieces !== to.pieces,
				len: from.lengthIn !== to.lengthIn,
				heat: from.heat !== to.heat,
				grade: from.grade !== to.grade,
				section: from.section !== to.section,
			})[f],
	);

const whole = (n: number) => Number.isInteger(n) && n > 0;

// 46 ft 14 in -> 47 ft 2 in
export const carryInches = (ft: string, inch: string) => {
	const inches = Number(inch);
	return inches >= 12
		? {
				ft: String((Number(ft) || 0) + Math.floor(inches / 12)),
				inch: String(inches % 12),
			}
		: { ft, inch };
};

export const invalid = (v: Values, f: Field): string | null => {
	if (f === "heat" && !v.heat) return "Heat is empty. Read it off the bars.";
	// the saw splits the tag's QR on "-"
	if (f === "heat" && v.heat.includes("-")) return 'Heat cannot have a "-".';
	if (f === "grade" && !v.grade) return "Pick the grade.";
	if (f === "pcs" && !whole(v.pieces))
		return "Pieces must be a whole number, more than 0.";
	if (f === "len" && !whole(v.lengthIn))
		return "Length must be whole inches, more than 0.";
	return null;
};

export const fmtLen = (inches: number) => formatMillLengthIn(inches) ?? "";

export const shown = (v: Values, f: Field): string =>
	({
		pcs: String(v.pieces),
		len: fmtLen(v.lengthIn),
		heat: v.heat,
		grade: v.grade,
		section: v.section,
	})[f];

export type SectionMap = Record<string, MillSection>;

// 2680 -> 2680 · 4" S I BEAM @ 7.7
export const sectionLabel = (name: string, sections: MillSection[]) => {
	const description = sections.find((s) => s.SectionName === name)?.Description;
	return description ? `${name} · ${description}` : name;
};

// null while the section has no lb/ft in the mill catalog
export const weightLbs = (v: Values, sections: SectionMap): number | null => {
	const lbft = sections[v.section]?.LBsPerFoot;
	return lbft == null
		? null
		: Math.round(((v.pieces * v.lengthIn) / 12) * lbft);
};

export const fmtLbs = (lbs: number | null) =>
	lbs == null ? "—" : `${lbs.toLocaleString("en-US")} lbs`;

// Layout the Saw Tablet scans: tag-section-grade-heat-<pcs>-<part>-length(in)-qty.
// No part at the mill: the saw finds the product by section, grade and length.
export const tagQrText = (tagNo: string, v: Values) =>
	[tagNo, v.section, v.grade, v.heat, "", "", v.lengthIn, v.pieces].join("-");

// Danieli first: main stacker. Riise: scrap, shorts, backup.
export const SOURCES = {
	Danieli: "Danieli stacker",
	Riise: "Riise stacker",
	Transfer: "Set on the transfer",
	Other: "Other",
} as const;

if (import.meta.env.DEV) {
	const v: Values = {
		section: "2680",
		grade: "47",
		heat: "79345",
		lengthIn: 558,
		pieces: 24,
	};
	const q = parseSawEntryQr(tagQrText("B-TS-000001", v));
	console.assert(
		q?.tagNo === "B-TS-000001" &&
			q.partNumber === null &&
			q.millLengthIn === 558 &&
			q.pieces === 24 &&
			q.heatNo === "79345",
		"Tag Station QR no longer parses on the Saw Tablet",
		q,
	);
}
